import { useAuth0 } from "@auth0/auth0-react";
import { useCallback } from "react";

import { Logger } from "@liberty-fund/logger";

import { analytics } from "@/analytics";
import { Button, Card } from "@/ui/components";
import { LFLogo } from "@/ui/icons";

interface UnauthorizedProps {
  attemptedEmail?: string;
  /** The deny reason from cloud-auth's post-login Action, prefix already stripped. */
  message?: string;
}

const DEFAULT_MESSAGE =
  "You don't have permission to access this portal with this account.";

/**
 * Shown in place of the generic auth error when Auth0 rejects the login with
 * `access_denied` — e.g. the post-login Action in cloud-auth determined the
 * account has no role for this app. Matches the design handoff in
 * `design_handoff_401_unauthorized/Unauthorized401.dc.html`.
 */
export default function Unauthorized({
  attemptedEmail,
  message = DEFAULT_MESSAGE,
}: UnauthorizedProps) {
  const { loginWithRedirect } = useAuth0();

  const handleTryAgain = useCallback(() => {
    analytics.track(
      "Login Started",
      { trigger: "retry" },
      { beforeUnload: true },
    );
    void loginWithRedirect({
      appState: {
        returnTo: `${window.location.pathname}${window.location.search}${window.location.hash}`,
      },
      // `access_denied` almost always means the user picked the wrong Google
      // account. Auth0/Google still hold an active SSO session for it, so a
      // plain redirect would silently sign back in and bounce them right
      // back here — `select_account` forces the chooser instead.
      authorizationParams: { prompt: "select_account" },
    }).catch((redirectError: unknown) => {
      Logger.error("Failed to restart sign-in from the unauthorized screen", {
        message: (redirectError as Error)?.message,
      });
    });
  }, [loginWithRedirect]);

  return (
    <main className="flex min-h-screen w-full flex-col items-center justify-center bg-gray-50 p-6">
      <img src={LFLogo.src} alt="Liberty Fund" className="mb-10 w-[150px]" />

      <Card className="w-full max-w-[440px] px-9 py-10 text-center">
        <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-[#FDEDED]">
          <svg
            width="26"
            height="24"
            viewBox="0 0 21 19"
            fill="none"
            className="text-lf-red-dark"
            aria-hidden="true"
          >
            <path
              d="M10.6077 7.747V11.497M1.30473 14.873C0.438731 16.373 1.52173 18.247 3.25273 18.247H17.9627C19.6927 18.247 20.7757 16.373 19.9107 14.873L12.5567 2.125C11.6907 0.625 9.52473 0.625 8.65873 2.125L1.30473 14.873ZM10.6077 14.497H10.6147V14.505H10.6077V14.497Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <h1 className="mb-2 text-[22px] leading-7 font-bold text-lf-blue-dark">
          401 — Access denied
        </h1>
        <p className="mb-5 text-sm leading-[22px] text-lf-blue-slate">
          {message}
        </p>

        {attemptedEmail && (
          <div className="mb-7 inline-flex max-w-full items-center gap-2 rounded-md bg-lf-gray px-3.5 py-2">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              className="shrink-0 text-lf-gray-dark"
              aria-hidden="true"
            >
              <path
                d="M3 6.5C3 5.67 3.67 5 4.5 5h15c.83 0 1.5.67 1.5 1.5v11c0 .83-.67 1.5-1.5 1.5h-15A1.5 1.5 0 0 1 3 17.5v-11Z"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path
                d="M4 6.5 12 13l8-6.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="wrap-anywhere text-left text-[13px] font-semibold text-lf-blue-dark">
              {attemptedEmail}
            </span>
          </div>
        )}

        <Button
          type="button"
          onClick={handleTryAgain}
          className="w-full justify-center"
        >
          Try again
        </Button>

        <p className="mt-5 text-xs text-lf-gray-dark">
          If you believe this is a mistake, contact your administrator.
        </p>
      </Card>
    </main>
  );
}
